import React, { ReactNode, useState } from 'react';
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import ApiKeyContext, { useApiKey } from './apiKey';

const scopes = [process.env.GATSBY_BOSCH_AUTH_OIDC_AUDIENCE || ''];

export function useToken(): {
  getToken: () => Promise<string>;
  displayName: string;
  familyName: string;
  givenName: string;
} {
  const { instance, accounts } = useMsal();
  const { apiKey } = useApiKey();
  const [accessToken, setAccessToken] = useState<null | string>(null);

  if (process.env.GATSBY_USE_API_KEY === 'true') {
    return { getToken: () => Promise.resolve(apiKey), displayName: '', familyName: '', givenName: '' };
  } else if (!accessToken && accounts.length > 0) {
    const request = {
      scopes,
      account: accounts[0],
    };
    setTimeout(() => {
      // removing the timeout causes rendering errors
      instance
        .acquireTokenSilent(request)
        .then(response => {
          setAccessToken(response.accessToken);
        })
        .catch(error => {
          // acquireTokenSilent can fail for a number of reasons, fallback to interaction
          if (error instanceof InteractionRequiredAuthError) {
            // instance.acquireTokenPopup(request).then(response => {
            //   setAccessToken(response.accessToken);
            // });
          }
        });
    });
  }

  const getToken = async (): Promise<string> => {
    if (!accounts[0]) {
      await instance.loginRedirect();
      return '';
    } else {
      return instance
        .acquireTokenSilent({
          scopes,
          account: accounts[0],
        })
        .then(res => {
          if ((res.idTokenClaims as { exp: number }).exp < Date.now() / 1000) {
            // idToken expired before access token, force token refresh
            return instance
              .acquireTokenSilent({
                scopes,
                account: accounts[0],
                forceRefresh: true,
              })
              .then(res => res.accessToken);
          }
          return res.accessToken;
        })
        .catch(async () => {
          await instance.acquireTokenRedirect({ scopes });
          return '';
        });
    }
  };

  return {
    getToken,
    displayName: accounts[0]?.name ?? '',
    familyName: (accounts[0]?.idTokenClaims?.family_name ?? '') as string,
    givenName: (accounts[0]?.idTokenClaims?.given_name ?? '') as string,
  };
}

export function BoschAuthProvider({ children }: { children: ReactNode }): ReactNode {
  if (process.env.GATSBY_USE_API_KEY === 'true') {
    return <ApiKeyContext>{children}</ApiKeyContext>;
  } else if (process.env.GATSBY_BOSCH_AUTH_CLIENT_ID) {
    if (typeof location !== 'undefined') {
      const pca = new PublicClientApplication({
        auth: {
          clientId: process.env.GATSBY_BOSCH_AUTH_CLIENT_ID!,
          redirectUri: process.env.GATSBY_BOSCH_AUTH_REDIRECT_URI!.replace('{host}', location.hostname),
          authority: `https://login.microsoftonline.com/${process.env.GATSBY_BOSCH_AUTH_TENANT_ID}`,
        },
      });

      return (
        <MsalProvider instance={pca}>
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={{ scopes }}
            errorComponent={() => <h1>Login failed</h1>}
          >
            {children}
          </MsalAuthenticationTemplate>
        </MsalProvider>
      );
    } else {
      return <div></div>; // this basically removes all benefits of SSG, but this is expected when hiding the content behind authentication
    }
  } else {
    // auth disabled
    return children;
  }
}
